import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

/** No repo has a shadcn `hover-card.tsx` yet — see pipelineHoverPopover's own doc comment. */
const HOVER_OPEN_DELAY_MS = 100
const HOVER_CLOSE_DELAY_MS = 25
/**
 * Radix's non-modal Popover content restores focus to the trigger on close
 * via `onCloseAutoFocus` (`PopoverContentNonModal`), including when nothing
 * was clicked at all — a purely hover-driven close still does this. If the
 * content also happens to unmount right under the cursor (e.g. rendered
 * flush against the trigger), the browser can additionally fire a fresh
 * `mouseenter` on the trigger that instant. Neither is a genuine new hover;
 * this window is how long after a hover-driven close both are ignored once.
 */
const REENTRY_GUARD_WINDOW_MS = 50

export interface PipelineHoverPopoverController {
  open: boolean
  /** Wire directly to `<Popover onOpenChange>` — applies instantly (click, Escape, outside click). */
  onOpenChange: (next: boolean) => void
  triggerHandlers: {
    onMouseEnter: () => void
    onMouseLeave: () => void
    onMouseDown: () => void
    onFocus: () => void
  }
  contentHandlers: {
    onMouseEnter: () => void
    onMouseLeave: () => void
    /** Wire to `<PopoverContent onCloseAutoFocus>` — see the module doc comment. */
    onCloseAutoFocus: (event: { preventDefault: () => void }) => void
  }
}

/**
 * Hover-intent controller shared by the Pipeline row popovers (invoices,
 * events) so the behavior isn't duplicated per cell. The repo has no
 * shadcn/Radix `hover-card.tsx` (which would give `openDelay`/`closeDelay`
 * natively) — until one exists, timers are the least-duplicated option.
 *
 * - Hovering the trigger opens after `HOVER_OPEN_DELAY_MS` — a cursor just
 *   passing over the trigger (e.g. scrolling down the list) never opens it.
 * - Leaving the trigger schedules a close after `HOVER_CLOSE_DELAY_MS`.
 *   Hovering the popover *content* within that window cancels the close, so
 *   crossing from trigger to content keeps it open. (Callers should also set
 *   `sideOffset={0}` on `PopoverContent` so there's no gap to cross in the
 *   first place — 25ms may not be enough time to cross a real gap.)
 * - A click (Radix's own trigger click-toggle, wired through `onOpenChange`)
 *   or keyboard focus opens immediately, bypassing the delay — required for
 *   keyboard/click accessibility. Mouse-driven focus (a click focuses its
 *   own button before the click event fires) is suppressed via
 *   `onMouseDown`, so a real mouse click doesn't flash open then immediately
 *   toggle closed by Radix's own click handler.
 *
 * Close-then-reopen loop (confirmed root cause, was reported as "it
 * disappears for a moment and then reopens"): Radix's non-modal Popover
 * content calls `triggerRef.current.focus()` in `onCloseAutoFocus` on
 * *every* close, hover-driven or not — see `PopoverContentNonModal` in
 * `@radix-ui/react-popover`. Before this fix, `onFocus` treated that
 * restored focus as a fresh user interaction and reopened immediately. Two
 * guards fix it: `onCloseAutoFocus` prevents the focus restore entirely for
 * a hover-driven close (there's no accessibility reason to move focus for a
 * close nothing was clicked or tabbed to), and a one-shot
 * `suppressNextFocusOpen` flag — armed on *every* close, hover or
 * keyboard/Escape — ignores the very next `onFocus` regardless of cause, so
 * even a close that's allowed to restore focus (Escape, for keyboard a11y)
 * cannot reopen the popover it just closed.
 */
export function usePipelineHoverPopover(): PipelineHoverPopoverController {
  const [open, setOpen] = useState(false)
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const suppressNextFocusOpen = useRef(false)
  const closeWasHoverDriven = useRef(false)
  const lastHoverCloseAt = useRef(0)

  const clearOpenTimer = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = undefined
    }
  }, [])

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = undefined
    }
  }, [])

  useEffect(
    () => () => {
      clearOpenTimer()
      clearCloseTimer()
    },
    [clearOpenTimer, clearCloseTimer],
  )

  const onOpenChange = useCallback(
    (next: boolean) => {
      clearOpenTimer()
      clearCloseTimer()
      if (!next) {
        // Escape / outside click / Radix's own trigger click-toggle: focus
        // is allowed to return to the trigger for accessibility, but that
        // restored focus must not reopen what it just closed.
        suppressNextFocusOpen.current = true
        closeWasHoverDriven.current = false
      }
      setOpen(next)
    },
    [clearOpenTimer, clearCloseTimer],
  )

  const openNow = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    setOpen(true)
  }, [clearOpenTimer, clearCloseTimer])

  const scheduleOpen = useCallback(() => {
    clearCloseTimer()
    if (open || openTimer.current) return
    // A `mouseenter` on the trigger firing the instant a hover-driven close
    // unmounts the content isn't a genuine new hover — ignore it once.
    if (Date.now() - lastHoverCloseAt.current < REENTRY_GUARD_WINDOW_MS) return
    openTimer.current = setTimeout(() => {
      openTimer.current = undefined
      setOpen(true)
    }, HOVER_OPEN_DELAY_MS)
  }, [open, clearCloseTimer])

  const scheduleClose = useCallback(() => {
    clearOpenTimer()
    if (!open) {
      clearCloseTimer()
      return
    }
    clearCloseTimer()
    closeTimer.current = setTimeout(() => {
      closeTimer.current = undefined
      suppressNextFocusOpen.current = true
      closeWasHoverDriven.current = true
      lastHoverCloseAt.current = Date.now()
      setOpen(false)
    }, HOVER_CLOSE_DELAY_MS)
  }, [open, clearOpenTimer, clearCloseTimer])

  const cancelScheduledClose = useCallback(() => {
    clearCloseTimer()
  }, [clearCloseTimer])

  return {
    open,
    onOpenChange,
    triggerHandlers: {
      onMouseEnter: scheduleOpen,
      onMouseLeave: scheduleClose,
      onMouseDown: () => {
        suppressNextFocusOpen.current = true
      },
      onFocus: () => {
        if (suppressNextFocusOpen.current) {
          suppressNextFocusOpen.current = false
          return
        }
        openNow()
      },
    },
    contentHandlers: {
      onMouseEnter: cancelScheduledClose,
      onMouseLeave: scheduleClose,
      onCloseAutoFocus: (event) => {
        if (closeWasHoverDriven.current) {
          event.preventDefault()
        }
      },
    },
  }
}

interface PipelinePopoverPanelProps {
  title: string
  /** Header count on the right — omitted while loading/erroring. */
  count?: number
  isLoading: boolean
  loadingText: string
  isError: boolean
  permissionDenied: boolean
  permissionDeniedText: string
  errorText: string
  isEmpty: boolean
  emptyText: string
  children: ReactNode
}

/**
 * Shared header/loading/error/empty shell for the Pipeline row popovers
 * (invoices, events) — only the row markup differs between them.
 */
export function PipelinePopoverPanel({
  title,
  count,
  isLoading,
  loadingText,
  isError,
  permissionDenied,
  permissionDeniedText,
  errorText,
  isEmpty,
  emptyText,
  children,
}: PipelinePopoverPanelProps) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-muted-foreground">
        <span>{title}</span>
        {!isLoading && !isError && count != null && <span className="font-financial">{count}</span>}
      </div>
      {isLoading ? (
        <div className="px-3 py-4 text-muted-foreground">{loadingText}</div>
      ) : permissionDenied ? (
        <div className="px-3 py-4 text-muted-foreground">{permissionDeniedText}</div>
      ) : isError ? (
        <div className="px-3 py-4 text-muted-foreground">{errorText}</div>
      ) : isEmpty ? (
        <div className="px-3 py-4 text-muted-foreground">{emptyText}</div>
      ) : (
        children
      )}
    </>
  )
}
